export type DesignCanvasMode = 'scroll' | 'fit' | 'clamp';

export type DesignCanvasScaleResult = {
  viewportWidth: number;
  mode: DesignCanvasMode;
  targetWidth: number;
  scale: number;
  baseHeight: number;
  designWidth: number;
  maxWidth: number;
};

export function setupDesignCanvasScale(opts: {
  root: HTMLElement | null;
  wrap: HTMLElement | null;
  canvas: HTMLElement | null;
  designWidth?: number;
  maxWidth?: number;
  onAfterApply?: (result: DesignCanvasScaleResult) => void;
}): { schedule: () => void; destroy: () => void } | null {
  const root = opts.root;
  const wrap = opts.wrap;
  const canvas = opts.canvas;

  if (!root || !wrap || !canvas) return null;

  const designWidth = opts.designWidth ?? 720;
  const maxWidth = opts.maxWidth ?? 1440;

  let rafId: number | null = null;
  let resizeObserver: ResizeObserver | null = null;

  const apply = (): void => {
    const viewportWidth = document.documentElement.clientWidth || window.innerWidth;
    let mode: DesignCanvasMode = 'scroll';
    let targetWidth = designWidth;

    if (viewportWidth < designWidth) {
      mode = 'fit';
      targetWidth = viewportWidth;
    } else if (viewportWidth > maxWidth) {
      mode = 'clamp';
      targetWidth = maxWidth;
    } else {
      mode = 'fit';
      targetWidth = viewportWidth;
    }

    const scale = targetWidth / designWidth;

    root.dataset.mode = mode;
    canvas.style.setProperty('--mj-scale', `${scale}`);

    const baseHeight = canvas.offsetHeight;
    wrap.style.width = `${targetWidth}px`;
    wrap.style.height = `${baseHeight * scale}px`;

    opts.onAfterApply?.({
      viewportWidth,
      mode,
      targetWidth,
      scale,
      baseHeight,
      designWidth,
      maxWidth,
    });
  };

  const schedule = (): void => {
    if (rafId !== null) return;
    rafId = window.requestAnimationFrame(() => {
      rafId = null;
      apply();
    });
  };

  window.addEventListener('resize', schedule, { passive: true });

  if (typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver(schedule);
    resizeObserver.observe(canvas);
  }

  schedule();

  const destroy = (): void => {
    if (rafId !== null) {
      window.cancelAnimationFrame(rafId);
      rafId = null;
    }
    window.removeEventListener('resize', schedule);
    resizeObserver?.disconnect();
    resizeObserver = null;
  };

  return { schedule, destroy };
}
